import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { clearCommitteeContext } from '@/utils/committeeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Set up axios interceptor for auth header
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const signup = async (email, password, firstName, lastName, role = 'applicant', additionalData = {}) => {
    const response = await axios.post(`${API}/auth/signup`, {
      email,
      password,
      first_name: firstName,
      last_name: lastName,
      role,
      ...additionalData
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const impersonateUser = async (userId) => {
    const response = await axios.post(`${API}/auth/impersonate`, { user_id: userId });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const stopImpersonation = async () => {
    const response = await axios.post(`${API}/auth/impersonate/stop`);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    if (user?.is_impersonating) {
      await stopImpersonation();
      return;
    }
    localStorage.removeItem('token');
    clearCommitteeContext();
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    // Force redirect to landing page
    window.location.href = '/';
  };

  // Set token directly (for OAuth, committee invites, etc.)
  const setAuthToken = (accessToken, userData = null) => {
    localStorage.setItem('token', accessToken);
    setToken(accessToken);
    if (userData) {
      setUser(userData);
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    signup,
    logout,
    impersonateUser,
    stopImpersonation,
    setAuthToken,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
